import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {BrandType} from '@shared/cci/features/const/environment';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

function ConsentBannerEu() {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<></>}>
        <Inner />
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * @link https://hmg.atlassian.net/browse/DRIVINGINS-2689
 * getVehicleServiceList 인터페이스 호출
 * 1) ccsOffline 값이 true이면 CCS Offline 배너 노출 (신규 배너 화면)
 * 2) serviceListAllEnabled 값이 true이면 배너 미노출
 * 3) ePrivacySupport값이 true이면 1-1 배너 노출
 * 4) ePrivacySupport값이 false이면 1-2 배너 노출
 */
function Inner() {
  const {
    data: {ePrivacySupported, serviceListAllEnabled, offlineModeEnabled},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(ccWebviewUtils.getVehicleServiceList)],
    queryFn: () => ccWebviewUtils.getVehicleServiceList(),
  });
  if (offlineModeEnabled) {
    return <OfflineModeBannerEu />;
  }
  if (serviceListAllEnabled) {
    return <></>;
  }
  if (ePrivacySupported) {
    return <ConsentBannerSupportedEu />;
  }
  return <ConsentBannerNotSupportedEu />;
}

function ConsentBannerSupportedEu() {
  const {t} = useInferableTranslation();
  const brand = import.meta.env.VITE_BRAND;
  return (
    <div className="guide_banner type_guide02">
      <span className="ic ic_filled_caution_16px" />
      <p className="guide_banner_title">{t('msg_dss_LN_service_currently_unavailable')}</p>
      <p className="guide_banner_desc">
        {t('msg_dss_LN_check_infotainment_system', {
          serviceName: brand === BrandType.KIA ? 'Kia Connect' : brand === BrandType.HYUNDAI ? 'Bluelink' : 'Genesis Connected Service',
        })}
      </p>
    </div>
  );
}

function ConsentBannerNotSupportedEu() {
  const {t} = useInferableTranslation();

  function linkToAppSettings() {
    WebviewUtils.close('app://serviceList');
  }

  return (
    <div className="guide_banner type_guide02">
      <span
        className="ic ic_filled_caution_16px"
        aria-hidden="true"
      />
      <p className="guide_banner_title">{t('msg_dss_LN_service_currently_unavailable')}</p>

      <button
        type="button"
        className="btn_hyperlink"
        onClick={() => linkToAppSettings()}
      >
        {t('msg_dss_LN_go_to_settings')}
        <span
          className="ic ic_arrow_forward_12px"
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

/**
 * EU 오프라인 모드 배너 컴포넌트
 *
 * @description
 * 차량에서 CCS 서비스 OFF(오프라인 모드) 시, 서비스 이용 제한 안내 배너를 노출합니다.
 * getVehicleServiceList 인터페이스를 호출하여 offlineModeEnabled 값이 true일 때 배너를 표시합니다.
 *
 * offlineModeEnabled (EU) 오프라인 모드 인지 여부
 * - true : 오프라인 모드
 * - false : 오프라인 모드 아님
 *
 * @related
 * - JIRA: https://hmg.atlassian.net/browse/DRIVINGINS-2689
 * - QA티켓: ICTQMSCHE-22096
 * - 앱 인터페이스: https://hmg.dooray.com/wiki/3621815477071999771/4033696064203499696
 *
 * @see getVehicleServiceList - 차량의 서비스 리스트 정보 취득
 */
function OfflineModeBannerEu() {
  const {t} = useInferableTranslation();
  return (
    <div className="guide_banner type_guide02">
      <span className="ic ic_filled_caution_16px" />
      <p className="guide_banner_title">{t('msg_home_offline_restricted_eu')}</p>
      <p className="guide_banner_desc">{t('msg_home_offline_relaunch_eu')}</p>
    </div>
  );
}

export default ConsentBannerEu;
